import { FC, useContext, useMemo } from 'react';
import { useNavigation } from 'react-navi';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@repo-level/core-front';

import { useTheme } from 'hooks/theme';
import {
  getUserVipGroup,
  PERCENT_BREACKPOINTS_STARDUST_RUSH,
  VIP_CURRENT_COLOR,
  VipGroupName,
} from 'modules/vip/components/helpers';
import { StardustRushContext } from 'contexts/stardustRush/stardustRush';
import { StardustRushStatus } from 'helpers/stardustRush';
import { replacePlaceholder } from 'helpers/cms';
import { StardustRushTimer } from 'modules/vip/components/StardustRush/components/StardustRushInfo/components/StardustRushTimer';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';

import { ScBonanzaPromoSection } from './styled';

import panelBg from 'assets/images/vip/panel-bg.webp';
import { ImageComponent } from 'components/Image';

export interface BonanzaPromoSectionProps {
  isMobile?: boolean;
  onLinkClick?: () => void;
}

export const StarDustRushPanel: FC<BonanzaPromoSectionProps> = ({
  isMobile = false,
  onLinkClick,
}) => {
  const {
    stardustRushStatus,
    userSDRProgressPercent,
    stardustRushCMSData,
    stardustRushStaticData,
    userTarget,
  } = useContext(StardustRushContext);
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const { navigate } = useNavigation();
  const { images } = useTheme();

  const userVipLevel = useMemo(() => {
    return getUserVipGroup(user?.statuses);
  }, [user]);

  const { isPVIP, isActive, isComplete } = useMemo(
    () => ({
      isPVIP: stardustRushStatus === StardustRushStatus.PVIP,
      isActive: stardustRushStatus === StardustRushStatus.ACTIVE,
      isComplete: stardustRushStatus === StardustRushStatus.COMPLETE,
    }),
    [stardustRushStatus]
  );

  const completeText = useMemo(() => {
    if (!stardustRushCMSData || !stardustRushStaticData) return '';
    return replacePlaceholder(
      stardustRushCMSData.gameCompleteText,
      'vip',
      stardustRushStaticData.vipTypeName
    );
  }, [stardustRushCMSData, stardustRushStaticData]);

  const targetText = useMemo(
    () =>
      isMobile ? stardustRushStaticData?.prizeText : 'vipNamePrizeSidebar',
    [isMobile, stardustRushStaticData]
  );

  const userGoalText = useMemo(
    () => `${t(targetText || '')}:${' '}${userTarget} CP`,
    [t, targetText, userTarget]
  );

  const nonActiveStatuses = (
    <>
      {isPVIP && (
        <p className="title complete">{stardustRushCMSData?.pVipText}</p>
      )}
      {isComplete && <p className="title complete">{completeText}</p>}
    </>
  );

  const imgSrc = useMemo(() => {
    if (!stardustRushStaticData) return '';
    const { sidebarPanelImg, menuPanelImg } = stardustRushStaticData;
    return isMobile ? menuPanelImg : sidebarPanelImg;
  }, [stardustRushStaticData, isMobile]);

  const imgConfig = useMemo(
    () => ({
      src: imgSrc,
      width: isMobile ? 56 : 128,
      height: isMobile ? 56 : 32,
      alt: 'logo',
    }),
    [isMobile, imgSrc]
  );
  const progressbar = (
    <SmallProgressBar
      userProgress={userSDRProgressPercent || 0}
      config={{
        dotsCount: PERCENT_BREACKPOINTS_STARDUST_RUSH.length,
        color: VIP_CURRENT_COLOR[userVipLevel || VipGroupName.GOLD],
      }}
    />
  );

  if (!stardustRushCMSData || !stardustRushStaticData) return null;

  return (
    <ScBonanzaPromoSection
      onClick={() => {
        onLinkClick && onLinkClick();
        navigate('/vip');
      }}
      isMobile={isMobile}
      bgMenu={panelBg}
    >
      <div className="info">
        <div className="addons">
          {imgSrc && <ImageComponent {...imgConfig} className="vip-logo" />}
          {isMobile && (
            <>
              {isActive && (
                <div className="text">
                  <span>
                    <b>{t('timeLeft')}: </b>
                    <StardustRushTimer />
                  </span>
                  <span>{userGoalText}</span>
                  {isMobile && progressbar}
                </div>
              )}
              {nonActiveStatuses}
            </>
          )}

          {isMobile && <images.arrowRightSmallIcon.component />}
        </div>
      </div>

      {!isMobile && (
        <>
          {isActive && (
            <>
              {progressbar}
              <p className="title under-progress-text">{userGoalText}</p>
            </>
          )}
          {nonActiveStatuses}
        </>
      )}
    </ScBonanzaPromoSection>
  );
};
